import React, { useState } from "react";
import { ChevronDown, ChevronRight, Sparkles, Info } from "lucide-react";

// Read-only Coverage Plan card (Stage 1.5 disclosure, founder-approved 2026-08-02).
// Shows the plan the generator will build FROM — one glance, zero required
// decisions. The teacher taps "Generate Unit" to proceed (default-accept). The
// per-lesson reasoning is one tap away (expand), but never gates the build.
// Editing is intentionally deferred — this ships the disclosure first.
const ROLE_STYLE = {
  "load-bearing": "bg-[#256e64]/12 text-[#256e64] border-[#256e64]/25",
  supporting: "bg-[#4a6984]/10 text-[#4a6984] border-[#4a6984]/20",
};

export const CoveragePlanCard = ({ summary, breakdown, model, onGenerate, onCancel, generating }) => {
  const [open, setOpen] = useState(false);
  if (!summary) return null;
  const strip = summary.per_expectation || [];
  const unaddressed = summary.unaddressed_expectations || [];
  const lessons = breakdown?.lessons || [];
  const modelLabel =
    model === "distributed" ? "Ongoing Task" : model === "test" ? "Test" : "Culminating Task";

  return (
    <div
      className="rounded-2xl border-2 border-[#1c2d3d]/12 bg-white p-6 shadow-sm"
      data-testid="coverage-plan-card"
    >
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 shrink-0 grid place-items-center rounded-lg bg-[#256e64]/12 text-[#256e64]">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-wide text-[#256e64]">
            Coverage Plan · {modelLabel}
          </p>
          <h3 className="mt-0.5 font-black text-base text-[#1c2d3d]" data-testid="coverage-plan-headline">
            {summary.num_lessons} lessons planned across {summary.expectation_count} expectation
            {summary.expectation_count === 1 ? "" : "s"}.
          </h3>

          {/* Per-expectation strip — stacks cleanly on phone */}
          <ul className="mt-3 space-y-1.5" data-testid="coverage-plan-strip">
            {strip.map((e) => (
              <li
                key={e.code}
                className="flex items-center gap-2 text-sm text-[#1c2d3d]"
                data-testid={`coverage-plan-exp-${e.code}`}
              >
                <span className="font-bold tabular-nums">{e.code}</span>
                <span className="text-[#4a6984]">·</span>
                <span className="text-[#4a6984]">
                  {e.lesson_count} lesson{e.lesson_count === 1 ? "" : "s"}
                </span>
                <span
                  className={`ml-auto rounded-full border px-2 py-0.5 text-[11px] font-bold ${
                    ROLE_STYLE[e.role] || ROLE_STYLE.supporting
                  }`}
                >
                  {e.role}
                </span>
              </li>
            ))}
          </ul>

          {/* Honesty note — only when relevant */}
          {unaddressed.length > 0 && (
            <div
              className="mt-3 flex items-start gap-2 rounded-lg bg-[#fffbeb] border border-[#b45309]/20 p-3"
              data-testid="coverage-plan-unaddressed"
            >
              <Info className="h-4 w-4 shrink-0 text-[#b45309] mt-0.5" />
              <p className="text-xs text-[#7c5310]">
                {unaddressed.length} selected expectation
                {unaddressed.length === 1 ? "" : "s"} ({unaddressed.join(", ")}) aren&apos;t required by
                this task, so they&apos;re intentionally not taught here — plan them into another unit.
              </p>
            </div>
          )}
          {summary.density_note && (
            <p className="mt-2 text-xs text-[#b45309]" data-testid="coverage-plan-density">
              {summary.density_note}
            </p>
          )}

          {/* Optional: read the per-lesson reasoning (1 tap, never required) */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#256e64] hover:underline"
            data-testid="coverage-plan-toggle"
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {open ? "Hide the lesson-by-lesson plan" : "See the lesson-by-lesson plan"}
          </button>
          {open && (
            <ol className="mt-2 space-y-2 border-l-2 border-[#1c2d3d]/10 pl-3" data-testid="coverage-plan-lessons">
              {lessons.map((l) => (
                <li key={l.index} className="text-xs text-[#4a6984]">
                  <span className="font-bold text-[#1c2d3d]">Lesson {l.index}</span>{" "}
                  <span className="rounded bg-[#1c2d3d]/6 px-1.5 py-0.5 text-[10px] font-bold uppercase">
                    {l.type} · {l.release_level}
                  </span>
                  {l.title_hint ? ` — ${l.title_hint}` : ""}
                  <div className="mt-0.5">
                    {(l.primary_expectations || []).length
                      ? `Teaches: ${(l.primary_expectations || []).join(", ")}`
                      : "Process lesson — no curriculum code"}
                    {l.builds_on ? ` · builds on Lesson ${l.builds_on}` : ""}
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              data-testid="coverage-plan-generate"
              className="inline-flex items-center gap-2 rounded-md bg-[#256e64] text-white px-5 py-2.5 text-sm font-black hover:bg-[#1e5a52] disabled:opacity-60"
            >
              <Sparkles className="h-4 w-4" />
              {generating ? "Building your unit…" : "Generate Unit"}
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={generating}
                data-testid="coverage-plan-cancel"
                className="text-sm font-bold text-[#4a6984] hover:underline disabled:opacity-60"
              >
                Back
              </button>
            )}
          </div>
          <p className="mt-2 text-[11px] text-[#4a6984]/70">
            This plan shapes your lessons — the unit builds from it.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoveragePlanCard;
